import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, ButtonVariant, Flex, Input, Modal, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { DEFAULT_SETTINGS, MAX_SLIPPAGE_BPS, MIN_SLIPPAGE_BPS } from 'utils/swap/types'
import type { SwapSettings } from 'utils/swap/types'

const SETTINGS_KEY = 'mprolab.swap.settings'
const PRESETS_BPS = [10, 50, 100]

/**
 * Settings survive a reload, and their absence is a working state.
 *
 * Everything here is read behind a try/catch and validated on the way back in:
 * a stored value is something a previous version of this page wrote, or
 * something the user edited by hand, and neither is a reason to swap on a
 * slippage of `NaN`.
 */
export function readSettings(): SwapSettings {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY)
    if (!raw) return DEFAULT_SETTINGS
    const parsed = JSON.parse(raw) as Partial<SwapSettings>
    return {
      slippageBps: clampSlippage(parsed.slippageBps ?? DEFAULT_SETTINGS.slippageBps),
      deadlineMinutes: clampDeadline(parsed.deadlineMinutes ?? DEFAULT_SETTINGS.deadlineMinutes)
    }
  } catch {
    return DEFAULT_SETTINGS
  }
}

export function writeSettings(settings: SwapSettings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
  } catch {
    // A preference we could not save is not worth a broken interaction.
  }
}

export function clampSlippage(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.slippageBps
  return Math.min(MAX_SLIPPAGE_BPS, Math.max(MIN_SLIPPAGE_BPS, Math.round(value)))
}

export function clampDeadline(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.deadlineMinutes
  return Math.min(60, Math.max(1, Math.round(value)))
}

const Presets = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

const Preset = styled.button<{ selected: boolean }>`
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid ${({ theme, selected }) => (selected ? theme.colors.cyan : theme.colors.gray8)};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.gray10};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`

interface SettingsModalProps {
  settings: SwapSettings
  onSave: (settings: SwapSettings) => void
  onDismiss?: () => void
}

const SettingsModal: React.FC<SettingsModalProps> = ({ settings, onSave, onDismiss }) => {
  const { t } = useTranslation()
  const [slippage, setSlippage] = useState(String(settings.slippageBps / 100))
  const [deadline, setDeadline] = useState(String(settings.deadlineMinutes))

  const currentBps = clampSlippage(Number(slippage.replace(',', '.')) * 100)
  const outOfRange =
    slippage.trim() !== '' && Math.round(Number(slippage.replace(',', '.')) * 100) !== currentBps

  return (
    <Modal onDismiss={onDismiss} title={t('swap.tradingParameters')}>
      <Flex flexDirection="column" gap="16px" width="380px" style={{ maxWidth: '100%' }}>
        <Flex flexDirection="column" gap="8px">
          <Text variant={TextVariant.TEXT_XS_REGULAR}>{t('swap.slippageTolerance')}</Text>
          <Presets>
            {PRESETS_BPS.map((bps) => (
              <Preset
                key={bps}
                type="button"
                selected={currentBps === bps}
                onClick={() => setSlippage(String(bps / 100))}
              >
                {bps / 100} %
              </Preset>
            ))}
          </Presets>
          <Input
            value={slippage}
            name="slippage"
            type="number"
            error={outOfRange}
            errorMsg={outOfRange ? t('swap.slippageOutOfRange') : undefined}
            onChange={(e) => setSlippage(e.target.value)}
          />
        </Flex>
        <Flex flexDirection="column" gap="8px">
          <Text variant={TextVariant.TEXT_XS_REGULAR}>{t('swap.deadlineMinutes')}</Text>
          <Input
            value={deadline}
            name="deadline"
            type="number"
            onChange={(e) => setDeadline(e.target.value)}
          />
        </Flex>
        <Flex gap="8px">
          <Button
            type="button"
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              const next = {
                slippageBps: currentBps,
                deadlineMinutes: clampDeadline(Number(deadline))
              }
              writeSettings(next)
              onSave(next)
              onDismiss?.()
            }}
          >
            {t('swap.saveSettings')}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.SECONDARY}
            onClick={() => {
              setSlippage(String(DEFAULT_SETTINGS.slippageBps / 100))
              setDeadline(String(DEFAULT_SETTINGS.deadlineMinutes))
            }}
          >
            {t('swap.resetToDefault')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

export default SettingsModal
